import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useCustomers } from "@/hooks/use-customers";
import { useAssignableUsers } from "@/hooks/use-users";
import { useCreateProject } from "@/hooks/use-create-project";
import { useNextProjectCode } from "@/hooks/use-next-project-code";
import {
  useNewProjectForm,
  type NewProjectInitialState,
} from "@/hooks/use-new-project-form";
import { StepperNav } from "@/components/new-project/StepperNav";
import { StepBasicInfo } from "@/components/new-project/StepBasicInfo";
import { StepProjectOwners } from "@/components/new-project/StepProjectOwners";
import { StepInvoicing } from "@/components/new-project/StepInvoicing";
import { StepBudget } from "@/components/new-project/StepBudget";
import { StepConfirmation } from "@/components/new-project/StepConfirmation";
import { BottomNav } from "@/components/new-project/BottomNav";

const STEPS_FIXED = [
  "Grunninfo",
  "Prosjekteiere",
  "Fakturering",
  "Tilbud og budsjett",
  "Bekreft og opprett",
];
// VIB-666: Time-jobber har ikke Tilbud-/budsjett-steg.
const STEPS_HOURLY = ["Grunninfo", "Prosjekteiere", "Fakturering", "Bekreft og opprett"];
// Internprosjekt: kun grunninfo + bekreft. Prosjekteiere settes automatisk
// (PL som 100% eier — samme mønster som CreateInternalProjectInlineForm).
const STEPS_INTERNAL = ["Grunninfo", "Bekreft og opprett"];

export interface NewProjectWizardProps {
  /**
   * Valgfri start-state. Brukes av ConvertOfferDialog for å prefille fra et
   * tilbud (kunde, prosjektnavn, totalbeløp, budsjettlinjer, fakturaterminer).
   */
  initialState?: Partial<NewProjectInitialState>;
  /**
   * Når true: kalles etter vellykket opprettelse i stedet for å navigere
   * automatisk til prosjektsiden. ConvertOfferDialog bruker dette for å gjøre
   * post-create-arbeid (oppdatere offers.project_id) før den lukker dialogen.
   */
  onCreated?: (result: { project_id: string; project_code: string }) => Promise<void> | void;
  /**
   * VIB-694: Skipp ett eller flere steg i wizarden.
   * - "upload" skjuler PDF-opplasteren i Grunninfo-steget.
   * - "budget" hopper over "Tilbud og budsjett"-steget (samme oppførsel som
   *   hourly-jobber: budsjettlinjene beholdes men redigeres på prosjektets
   *   Timetildeling-tab etter opprettelse).
   * Brukes av ConvertOfferDialog som har budsjettet ferdig fra tilbudet.
   */
  skipSteps?: ("upload" | "budget")[];
  /**
   * Når true skjules «Internt prosjekt»/«Time-jobb»-bryterne i Grunninfo.
   * ConvertOfferDialog setter dette — tilbud-avledede prosjekter er alltid
   * eksterne + fastpris.
   */
  fromOffer?: boolean;
}

/**
 * VIB-686: Felles wizard for "Nytt prosjekt" (full /nytt-prosjekt-side) og
 * "Konverter tilbud til prosjekt" (modal fra OfferEditor). Forskjellen er
 * kun initialState — selve flyten, validering og DB-skriving er felles.
 */
export function NewProjectWizard({
  initialState,
  onCreated,
  skipSteps = [],
  fromOffer = false,
}: NewProjectWizardProps) {
  const { data: customers } = useCustomers();
  const { data: users } = useAssignableUsers();
  const { createProject, loading: createLoading } = useCreateProject({
    onSuccess: onCreated,
  });
  const hideOfferUpload = skipSteps.includes("upload");
  const skipBudgetStep = skipSteps.includes("budget");
  const form = useNewProjectForm({
    customers,
    nextCode: "",
    initialState,
    skipBudgetStep,
  });
  const { code: nextCode, loading: codeLoading } = useNextProjectCode(form.isInternal);

  const handleCreate = async () => {
    // VIB-686: Lagre invoice_terms som source-of-truth, men også
    // startup_invoice_pct for backward compat med 23+ filer som leser den.
    const startupPct = form.invoiceTerms[0]?.pct ?? 60;
    const payload = {
      project_code: form.projectCode,
      name: form.projectName,
      customer_id: form.customerId || undefined,
      total_amount: form.total,
      estimated_deadline: form.deadline || undefined,
      estimated_start_date: form.estimatedStartDate || undefined,
      startup_invoice_pct: startupPct,
      invoice_terms: form.invoiceTerms,
      is_internal: form.isInternal,
      project_type: form.projectType,
      project_status: form.projectStatus,
      production_lead_id: form.productionLeadId || undefined,
      payment_due_days: form.paymentDueDays,
      invoice_email: form.invoiceEmail || undefined,
      // VIB-1191: deres_ref/deres_ref_kode er kanoniske faktureringsref-felt.
      deres_ref: form.customerReferencePerson || undefined,
      deres_ref_kode: form.customerReferenceNumber || undefined,
      bills_travel_per_medgatt: form.billsTravelPerMedgatt,
      // Fan-out: hver salgsenhet → én project_owners-rad per medlem,
      // ownership_pct = unit_pct / antall medlemmer. Da fungerer provisjons-
      // beregningen rett ut av boksen for individuelle medlemmer, samtidig
      // som sales_unit_id beholdes for gruppering.
      //
      // Internprosjekt har ingen salgsenheter/salgseier (ingen provisjon å
      // fordele) → tom owners-array. Salgseier er valgfri for alle prosjekter.
      owners: form.isInternal
        ? []
        : form.selectedSalesUnits.flatMap((u) => {
            const memberCount = u.members.length;
            if (memberCount === 0) return [];
            const memberPct = u.ownershipPct / memberCount;
            return u.members.map((m) => ({
              user_id: m.user_id,
              sales_unit_id: u.salesUnitId,
              ownership_pct: memberPct,
            }));
          }),
      // VIB-666: time-jobber sender ingen budsjett-linjer.
      budget_lines:
        form.projectType === "hourly"
          ? []
          : form.budgetLines.map((l) => ({
              phase: l.phase,
              description: l.description,
              quantity_hours: l.quantity_hours,
              amount: l.amount,
              estimated_cost: l.estimated_cost,
              // VIB-584: cost_type kommer fra mal-match; legacy faller tilbake
              // til has_time-heuristikk.
              cost_type: l.cost_type ?? (l.has_time ? "internal" : "external"),
              template_id: l.template_id ?? null,
              // VIB-995: bevar tilvalg-markering fra konvertering.
              is_optional: l.is_optional ?? false,
              // VIB-1158: bevar tilvalgs-seksjonsnavn for per-option rubrikk.
              option_label: l.option_label ?? null,
              // VIB-2866: tilbudets rekkefølge innen fase. Uten denne sorteres
              // Økonomi-tabben på created_at, som er lik for alle linjene.
              sort_order: l.sort_order ?? null,
            })),
      // Overhead/rabatt fra tilbudet skrives til project_offer_metadata av
      // create-project. Sendes KUN ved konvertering (fromOffer) — et manuelt
      // prosjekt skal ikke få en 0%-overhead-rad som overstyrer default.
      // Feedback 25.06: overhead + rabatt kom ikke med ved konvertering.
      ...(fromOffer && {
        offer_metadata: {
          offer_total: form.total,
          overhead_pct: form.offerOverheadPct,
          discount_pct: form.offerDiscountPct,
          discount_amount: form.offerDiscountAmount,
          startup_invoice_pct: startupPct,
          final_invoice_pct: form.invoiceTerms[form.invoiceTerms.length - 1]?.pct ?? 40,
          include_travel_in_overhead: form.includeTravelInOverhead === true,
        },
      }),
    };
    await createProject(payload);
  };

  const isHourly = form.projectType === "hourly";
  const isInternal = form.isInternal;
  // VIB-694: skipBudget gjelder både hourly OG når wizard-eier (f.eks.
  // ConvertOfferDialog) eksplisitt ber om å hoppe over budsjett-steget.
  // Internprosjekt skipper både Fakturering OG Budsjett.
  const skipBudget = isHourly || skipBudgetStep || isInternal;
  const steps = isInternal ? STEPS_INTERNAL : skipBudget ? STEPS_HOURLY : STEPS_FIXED;
  // Step-mapping for stepper og navigasjon:
  // - Internal: form.step 0→0, 4→1 (2 visible steps — Grunninfo og Bekreft)
  // - Hourly/skipBudget: form.step 0..2 direkte, 4→3 (4 visible steps)
  // - Fixed: form.step 0..4 direkte (5 visible steps)
  const formStepToDisplay = (formStep: number): number => {
    if (isInternal) {
      if (formStep === 4) return 1;
      return 0;
    }
    if (skipBudget && formStep === 4) return 3;
    return formStep;
  };
  const displayStepToForm = (displayIdx: number): number => {
    if (isInternal) {
      if (displayIdx === 1) return 4;
      return 0;
    }
    if (skipBudget && displayIdx === 3) return 4;
    return displayIdx;
  };
  const displayStep = formStepToDisplay(form.step);
  const handleStepClick = (displayIdx: number) => {
    form.setStep(displayStepToForm(displayIdx));
  };

  // VIB-686: blokker "Neste"/"Opprett" når faktureringssteget er aktivt og
  // sum != 100 %. Internprosjekt hopper over steget, så validering gjelder
  // ikke der.
  const nextDisabled = !isInternal && form.step === 2 && !form.invoiceTermsValid;
  const createDisabled = !isInternal && !form.invoiceTermsValid;

  return (
    <>
      <StepperNav
        steps={steps}
        currentStep={displayStep}
        onStepClick={handleStepClick}
      />
      <Card>
        <CardContent className="pt-6">
          {form.step === 0 && (
            <StepBasicInfo
              customers={customers}
              users={users}
              customerId={form.customerId}
              onCustomerChange={form.setCustomerId}
              projectName={form.projectName}
              onProjectNameChange={form.setProjectName}
              projectCode={form.projectCode || nextCode}
              projectCodeLoading={codeLoading}
              totalAmount={form.totalAmount}
              onTotalAmountChange={form.setTotalAmount}
              deadline={form.deadline}
              onDeadlineChange={form.setDeadline}
              estimatedStartDate={form.estimatedStartDate}
              onEstimatedStartDateChange={form.setEstimatedStartDate}
              isInternal={form.isInternal}
              onIsInternalChange={form.setIsInternal}
              projectType={form.projectType}
              onProjectTypeChange={form.setProjectType}
              projectStatus={form.projectStatus}
              onProjectStatusChange={form.setProjectStatus}
              productionLeadId={form.productionLeadId}
              onProductionLeadChange={form.setProductionLeadId}
              pdfUploaded={form.pdfUploaded}
              budgetLinesCount={form.budgetLines.length}
              onPdfReset={() => form.setPdfUploaded(false)}
              aiProgress={form.aiProgress}
              aiLoading={form.aiLoading}
              onOfferSelected={form.applyOffer}
              stepErrors={form.stepErrors}
              hideOfferUpload={hideOfferUpload}
              hideTypeToggles={fromOffer}
            />
          )}
          {form.step === 1 && (
            <StepProjectOwners
              selectedSalesUnits={form.selectedSalesUnits}
              onToggleSalesUnit={form.toggleSalesUnit}
            />
          )}
          {form.step === 2 && (
            <StepInvoicing
              totalAmount={form.total}
              invoiceTerms={form.invoiceTerms}
              onInvoiceTermsChange={form.setInvoiceTerms}
              paymentDueDays={form.paymentDueDays}
              onPaymentDueDaysChange={form.setPaymentDueDays}
              invoiceEmail={form.invoiceEmail}
              onInvoiceEmailChange={form.setInvoiceEmail}
              customerReferencePerson={form.customerReferencePerson}
              onCustomerReferencePersonChange={form.setCustomerReferencePerson}
              customerReferenceNumber={form.customerReferenceNumber}
              onCustomerReferenceNumberChange={form.setCustomerReferenceNumber}
              billsTravelPerMedgatt={form.billsTravelPerMedgatt}
              onBillsTravelPerMedgattChange={form.setBillsTravelPerMedgatt}
              projectType={form.projectType}
            />
          )}
          {form.step === 3 && !skipBudget && (
            <StepBudget
              budgetLines={form.budgetLines}
              aiParsedValues={form.aiParsedValues}
              onUpdateBudgetLine={(line) =>
                form.setBudgetLines((prev) =>
                  prev.map((l) => (l.id === line.id ? line : l)),
                )
              }
              onDeleteBudgetLine={(id) =>
                form.setBudgetLines((prev) => prev.filter((l) => l.id !== id))
              }
              onAddBudgetLine={(phase) =>
                form.setBudgetLines((prev) => [
                  ...prev,
                  {
                    id: String(Math.random()),
                    phase,
                    description: "",
                    quantity_hours: 0,
                    amount: 0,
                    estimated_cost: 0,
                    has_time: true,
                    cost_type: "internal",
                    template_id: null,
                    template_name: null,
                  },
                ])
              }
              budgetTotal={form.budgetTotal}
              totalEstCost={form.totalEstCost}
              totalHours={form.totalHours}
              calculatedTotal={form.calculatedTotal}
              validationDiff={form.validationDiff}
              offerOverheadPct={form.offerOverheadPct}
              offerDiscountPct={form.offerDiscountPct}
              offerDiscountAmount={form.offerDiscountAmount}
            />
          )}
          {form.step === 4 && (
            <StepConfirmation
              customers={customers}
              customerId={form.customerId}
              projectName={form.projectName}
              projectCode={form.projectCode || nextCode}
              projectStatus={form.projectStatus}
              totalAmount={form.total}
              deadline={form.deadline}
              isInternal={form.isInternal}
              users={users}
              productionLeadId={form.productionLeadId}
              customerContactPerson={form.selectedCustomer?.contact_person}
              customerContactEmail={form.selectedCustomer?.contact_email}
              selectedSalesUnits={form.selectedSalesUnits}
              startAmount={form.startAmount}
              paymentDueDays={form.paymentDueDays}
              invoiceEmail={form.invoiceEmail}
              customerReferencePerson={form.customerReferencePerson}
              customerReferenceNumber={form.customerReferenceNumber}
              budgetLinesCount={form.budgetLines.length}
              budgetTotal={form.budgetTotal}
              totalHours={form.totalHours}
            />
          )}
          <BottomNav
            step={displayStep}
            totalSteps={steps.length}
            onPrevious={() =>
              form.setStep((s) => {
                // Internprosjekt: gå fra Bekreft (4) tilbake til Grunninfo (0).
                if (isInternal && s === 4) return 0;
                // VIB-666/694: hopp over Budsjett-steget bakover når skippet.
                if (skipBudget && s === 4) return 2;
                return s - 1;
              })
            }
            onNext={form.handleNext}
            onCreate={handleCreate}
            loading={createLoading}
            aiLoading={form.aiLoading}
            nextDisabled={nextDisabled}
            createDisabled={createDisabled}
          />
        </CardContent>
      </Card>
    </>
  );
}
